import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  CreditCard,
  Home,
  Landmark,
  PiggyBank,
  Sparkles,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { FEATURED_INSTITUTIONS_BY_REGION, getFeaturedInstitutions } from '../../config/featuredInstitutions';
import { getInstitutionLogoFallbackUrl } from '../../utils/institutionLogoFallbacks';
import Button from '../ui/Button';

export const REGION_INSTITUTIONS = FEATURED_INSTITUTIONS_BY_REGION;

const shellClassName = [
  'onboarding-visual relative overflow-hidden rounded-[28px] border border-white/[0.08]',
  'bg-[linear-gradient(180deg,rgba(32,43,65,0.9),rgba(15,23,42,0.96))]',
  'shadow-[0_0_0_1px_rgba(99,102,241,0.08),0_24px_64px_rgba(2,6,23,0.42)]',
].join(' ');

const gridOverlayClassName =
  'pointer-events-none absolute inset-0 opacity-45 [background-image:linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] [background-size:44px_44px]';

const glowClassName =
  'pointer-events-none absolute -top-20 left-1/2 h-52 w-[75%] -translate-x-1/2 rounded-full bg-indigo-500/18 blur-3xl';

const getLogoTreatment = (naturalWidth, naturalHeight) => {
  const aspectRatio = naturalHeight > 0 ? naturalWidth / naturalHeight : 1.6;

  if (aspectRatio >= 3.1) return 'wordmark';
  if (aspectRatio >= 1.8) return 'wide';
  if (aspectRatio <= 1.05) return 'mark';
  return 'balanced';
};

const InstitutionLogoTile = ({
  institution,
  region = 'CA',
  onSelect = null,
  disabled = false,
  compact = false,
  showLabel = false,
}) => {
  const fallbackLogoUrl = getInstitutionLogoFallbackUrl([
    institution.name,
    institution.searchName,
    institution.domain,
  ]);
  const logoCandidates = [
    institution.logoUrl,
    institution.logo_url,
    institution.logo,
    institution.localLogo ? `${process.env.PUBLIC_URL || ''}${institution.localLogo}` : null,
    fallbackLogoUrl,
  ].filter(Boolean);
  const [logoIndex, setLogoIndex] = useState(0);
  const [didLoadFail, setDidLoadFail] = useState(false);
  const [logoTreatment, setLogoTreatment] = useState('balanced');
  const logoUrl = logoCandidates[logoIndex];
  const logoScale = Number(institution.logoScale);
  const logoStyle = Number.isFinite(logoScale) && logoScale > 0 && logoScale !== 1
    ? { transform: `scale(${logoScale})` }
    : undefined;

  const tileSurfaceClassName = 'onboarding-bank-tile';
  const labelClassName = 'text-[var(--color-text-primary)]';
  const fallbackLabelClassName = labelClassName;
  const logoFrameClassName = showLabel
    ? (logoTreatment === 'wordmark'
      ? 'h-[24px] w-[78%] sm:h-[28px]'
      : logoTreatment === 'wide'
        ? 'h-[28px] w-[68%] sm:h-[32px]'
        : logoTreatment === 'mark'
          ? 'h-[38px] w-[38px] sm:h-[44px] sm:w-[44px]'
          : 'h-[30px] w-[56%] sm:h-[34px]')
    : compact
      ? (logoTreatment === 'wordmark'
        ? 'h-[22px] w-[84%]'
        : logoTreatment === 'wide'
          ? 'h-[26px] w-[76%]'
          : logoTreatment === 'mark'
            ? 'h-[36px] w-[36px]'
            : 'h-[28px] w-[64%]')
      : (logoTreatment === 'wordmark'
        ? 'h-[30px] w-[84%] sm:h-[36px]'
        : logoTreatment === 'wide'
          ? 'h-[34px] w-[74%] sm:h-[40px]'
          : logoTreatment === 'mark'
            ? 'h-[48px] w-[48px] sm:h-[56px] sm:w-[56px]'
            : 'h-[38px] w-[62%] sm:h-[44px]');
  const tileSizeClassName = showLabel
    ? 'min-h-[94px] rounded-[20px] px-3 py-3 sm:min-h-[104px] sm:px-4'
    : compact
      ? 'min-h-[76px] rounded-[18px] px-2.5 py-2 sm:min-h-[84px] sm:px-3'
      : 'min-h-[96px] rounded-[22px] px-4 py-3 sm:min-h-[108px] sm:px-5';

  const interactiveClassName = onSelect
    ? (disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')
    : 'cursor-default';

  const logoOrFallback = (
    <>
      {!didLoadFail ? (
        <div className={`flex items-center justify-center ${logoFrameClassName}`}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={institution.name}
              className="h-full w-full object-contain"
              style={logoStyle}
              loading="lazy"
              referrerPolicy="no-referrer"
              onLoad={(event) => {
                setLogoTreatment(getLogoTreatment(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight));
              }}
              onError={() => {
                if (logoIndex < logoCandidates.length - 1) {
                  setLogoIndex((current) => current + 1);
                  return;
                }
                setDidLoadFail(true);
              }}
            />
          ) : (
            <span className={`px-2 text-center font-semibold ${fallbackLabelClassName} ${showLabel ? 'text-[11px] sm:text-xs' : 'text-xs sm:text-sm'}`}>{institution.name}</span>
          )}
        </div>
      ) : (
        <span className={`px-2 text-center font-semibold ${fallbackLabelClassName} ${showLabel ? 'text-[11px] sm:text-xs' : 'text-xs sm:text-sm'}`}>{institution.name}</span>
      )}
    </>
  );
  const content = showLabel ? (
    <div className="flex flex-col items-center justify-center gap-2.5">
      {logoOrFallback}
      {!didLoadFail ? (
        <span className={`px-1 text-center text-[11px] font-semibold sm:text-xs ${labelClassName}`}>{institution.name}</span>
      ) : null}
    </div>
  ) : logoOrFallback;

  if (onSelect) {
    return (
      <Button
        type="button"
        variant="unstyled"
        size="none"
        onClick={() => onSelect(institution)}
        disabled={disabled}
        aria-label={`Connect ${institution.name}`}
        className={`flex w-full items-center justify-center overflow-hidden border ${tileSurfaceClassName} ${tileSizeClassName} transition-transform duration-200 ${interactiveClassName}`}
      >
        {content}
      </Button>
    );
  }

  return (
    <div
      className={`flex w-full items-center justify-center overflow-hidden border ${tileSurfaceClassName} ${tileSizeClassName} transition-transform duration-200 ${interactiveClassName}`}
    >
      {content}
    </div>
  );
};

const SceneShell = ({ eyebrow, title, description, children, className = '' }) => (
  <div className={`${shellClassName} ${className}`}>
    <div className={glowClassName} />
    <div className={gridOverlayClassName} />
    <div className="relative space-y-5 p-5 sm:p-6">
      {(eyebrow || title || description) ? (
        <div className="space-y-3">
          {eyebrow ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-indigo-400/20 bg-indigo-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-200">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-300" />
              {eyebrow}
            </div>
          ) : null}
          {title ? <h3 className="text-lg font-semibold text-white sm:text-xl">{title}</h3> : null}
          {description ? <p className="max-w-xl text-sm leading-6 text-slate-400">{description}</p> : null}
        </div>
      ) : null}
      {children}
    </div>
  </div>
);

export const InstitutionLogoGrid = ({
  region = 'US',
  institutions = null,
  label,
  className = '',
  plain = false,
  onInstitutionSelect = null,
  disabled = false,
}) => {
  const resolvedInstitutions = Array.isArray(institutions) && institutions.length > 0
    ? institutions
    : getFeaturedInstitutions(region);

  if (plain) {
    return (
      <div className={`flex justify-center ${className}`}>
        <div className="grid w-full grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4">
          {resolvedInstitutions.map((institution, index) => (
            <motion.div
              key={institution.slug || institution.name}
              initial={{ opacity: 0, scale: 0.88, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.38, delay: index * 0.065, ease: [0.16, 1, 0.3, 1] }}
            >
              <InstitutionLogoTile
                institution={institution}
                region={region}
                onSelect={onInstitutionSelect}
                disabled={disabled}
                showLabel
              />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <SceneShell
      title="Connect the accounts you already use."
      description="Pick up where your real banking life already lives. Vylo keeps every connection read-only and routes you through the right provider for that institution."
      className={className}
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
        {resolvedInstitutions.map((institution) => (
          <InstitutionLogoTile
            key={institution.slug || institution.name}
            institution={institution}
            region={region}
            onSelect={onInstitutionSelect}
            disabled={disabled}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
        <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">Read-only access</span>
        <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">12,000+ institutions</span>
        <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">Trusted provider routing</span>
      </div>
    </SceneShell>
  );
};

export const AppPreviewScene = ({ className = '' }) => (
  <SceneShell
    eyebrow="Runs itself"
    title="One clear system. No cleanup weekend."
    description="The same signals from the website hero, now inside onboarding: spending, categorization, and budget progress all tell one coherent story."
    className={className}
  >
    <div className="grid gap-3 sm:grid-cols-[1.2fr_0.9fr]">
      <div className="rounded-3xl border border-white/[0.08] bg-slate-950/55 p-4">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">March spend</p>
            <p className="mt-1 text-2xl font-bold text-white">$3,241<span className="text-base text-white/35">.58</span></p>
          </div>
          <div className="rounded-2xl border border-indigo-400/20 bg-indigo-500/10 p-3 text-indigo-200">
            <Wallet className="h-5 w-5" />
          </div>
        </div>
        <div className="mb-4 flex h-24 items-end gap-2">
          {[42, 58, 35, 72, 49, 88, 66].map((height, index) => (
            <div
              key={height}
              className={`flex-1 rounded-t-[10px] ${index === 5 ? 'bg-indigo-500' : 'bg-indigo-500/30'}`}
              style={{ height: `${height}%` }}
            />
          ))}
        </div>
        <div className="space-y-2">
          {[
            ['Groceries', '$842', 'bg-emerald-400'],
            ['Dining out', '$614', 'bg-amber-400'],
            ['Transport', '$390', 'bg-indigo-400'],
          ].map(([label, amount, accent]) => (
            <div key={label} className="flex items-center justify-between rounded-2xl border border-white/[0.06] bg-white/[0.03] px-3 py-2">
              <div className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${accent}`} />
                <span className="text-sm text-slate-200">{label}</span>
              </div>
              <span className="text-sm font-semibold text-white">{amount}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-3">
        <div className="rounded-3xl border border-white/[0.08] bg-white/[0.04] p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-2.5 text-emerald-200">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Auto-categorized</p>
              <p className="text-xs text-slate-400">The repetitive work is already gone.</p>
            </div>
          </div>
          <div className="space-y-2">
            {[
              ['Whole Foods', 'Groceries'],
              ['Uber', 'Transport'],
              ['Netflix', 'Subscriptions'],
            ].map(([merchant, category]) => (
              <div key={merchant} className="rounded-2xl border border-white/[0.06] bg-slate-950/40 px-3 py-2">
                <p className="text-sm font-medium text-white">{merchant}</p>
                <p className="text-xs text-slate-400">→ {category}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl border border-white/[0.08] bg-white/[0.04] p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="rounded-2xl border border-indigo-400/20 bg-indigo-500/10 p-2.5 text-indigo-200">
              <ArrowRight className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Budgets stay in view</p>
              <p className="text-xs text-slate-400">Progress surfaces without extra work.</p>
            </div>
          </div>
          <div className="space-y-3">
            {[
              ['Groceries', '84%', 'bg-emerald-400'],
              ['Dining out', '100%', 'bg-amber-400'],
            ].map(([label, percent, accent]) => (
              <div key={label}>
                <div className="mb-1 flex justify-between text-xs text-slate-300">
                  <span>{label}</span>
                  <span>{percent}</span>
                </div>
                <div className="h-2 rounded-full bg-white/10">
                  <div className={`h-2 rounded-full ${accent}`} style={{ width: percent }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </SceneShell>
);

export const AppWorkspaceScene = ({ className = '', focus = 'overview' }) => {
  const navItems = [
    { label: 'Dashboard', active: focus !== 'household' },
    { label: 'Transactions', active: false },
    { label: 'Budgets', active: false },
    { label: 'Household', active: focus === 'household' },
  ];
  const statCards = [
    { accent: 'bg-indigo-300/70', width: 'w-20' },
    { accent: 'bg-emerald-300/70', width: 'w-24' },
    { accent: 'bg-cyan-300/70', width: 'w-16' },
  ];

  return (
    <div className={`relative h-full min-h-[620px] overflow-hidden ${className}`}>
      <div className="absolute inset-0 bg-[#0d1725]" />
      <div className="pointer-events-none absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] [background-size:36px_36px]" />
      <div className="pointer-events-none absolute left-[220px] top-[84px] h-[340px] w-[340px] rounded-full bg-indigo-500/12 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[80px] right-[60px] h-[300px] w-[300px] rounded-full bg-cyan-400/10 blur-3xl" />

      <div className="absolute inset-y-0 left-0 w-[228px] border-r border-white/[0.08] bg-[#101926]" />
      <div className="absolute left-[228px] right-0 top-0 h-[88px] border-b border-white/[0.08] bg-[#121c2b]" />

      <div className="absolute left-0 top-0 w-[228px] px-5 py-6">
        <div className="mb-8 h-10 w-[116px] rounded-[14px] bg-white/[0.16]" />
        <div className="space-y-2">
          {navItems.map((item) => (
            <div
              key={item.label}
              className={`flex items-center gap-3 rounded-[16px] px-3 py-3 ${
                item.active
                  ? 'bg-white/[0.14] shadow-[inset_2px_0_0_rgba(255,255,255,0.52)]'
                  : 'bg-white/[0.05]'
              }`}
            >
              <div className={`h-8 w-8 rounded-xl ${item.active ? 'bg-indigo-300/35' : 'bg-white/[0.09]'}`} />
              <div className={`h-3 rounded-full ${item.active ? 'w-24 bg-white/[0.28]' : 'w-20 bg-white/[0.18]'}`} />
            </div>
          ))}
        </div>
        <div className="mt-8 border-t border-white/[0.08] pt-6">
          <div className="flex items-center gap-3 rounded-[16px] bg-white/[0.06] px-3 py-3">
            <div className="h-8 w-8 rounded-xl bg-white/[0.09]" />
            <div className="h-3 w-16 rounded-full bg-white/[0.18]" />
          </div>
        </div>
      </div>

      <div className="absolute left-[254px] right-[32px] top-[22px] flex h-[44px] items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-32 rounded-[14px] bg-white/[0.13]" />
          <div className="h-10 w-24 rounded-[14px] bg-white/[0.08]" />
          <div className="h-10 w-28 rounded-[14px] bg-white/[0.08]" />
        </div>
        <div className="flex items-center gap-3">
          <div className="h-10 w-32 rounded-[14px] bg-white/[0.08]" />
          <div className="h-10 w-10 rounded-full bg-white/[0.16]" />
        </div>
      </div>

      <div className="absolute left-[254px] right-[32px] top-[118px] grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <div className="rounded-[32px] border border-white/[0.10] bg-[#182233] px-7 py-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          <div className="mb-6 flex items-start justify-between">
            <div className="space-y-3">
              <div className="h-3 w-28 rounded-full bg-white/[0.16]" />
              <div className="h-8 w-44 rounded-full bg-white/[0.26]" />
            </div>
            <div className="flex gap-2">
              {statCards.map(({ accent, width }, index) => (
                <div key={`backdrop-stat-card-${index}`} className="rounded-[18px] border border-white/[0.08] bg-[#202c3d] px-3 py-3">
                  <div className={`mb-2 h-2.5 rounded-full ${width} bg-white/[0.16]`} />
                  <div className={`h-6 rounded-full ${accent} ${width}`} />
                </div>
              ))}
            </div>
          </div>
          <div className="mb-5 flex h-32 items-end gap-3">
            {[34, 46, 40, 68, 52, 84, 66].map((height, index) => (
              <div
                key={`workspace-spend-${height}`}
                className={`flex-1 rounded-t-[14px] ${index === 5 ? 'bg-indigo-300/74' : 'bg-white/[0.18]'}`}
                style={{ height: `${height}%` }}
              />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[0, 1, 2].map((item) => (
              <div key={`backdrop-summary-${item}`} className="rounded-[18px] border border-white/[0.08] bg-[#202c3d] px-4 py-4">
                <div className="mb-3 h-3 w-20 rounded-full bg-white/[0.15]" />
                <div className="h-7 w-24 rounded-full bg-white/[0.22]" />
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-6">
          <div className="rounded-[30px] border border-white/[0.10] bg-[#182233] px-6 py-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="space-y-3">
                <div className="h-3 w-24 rounded-full bg-white/[0.16]" />
                <div className="h-6 w-36 rounded-full bg-white/[0.22]" />
              </div>
              <div className="h-10 w-10 rounded-[16px] bg-emerald-300/28" />
            </div>
            <div className="space-y-4">
              {[60, 78, 64].map((width, index) => (
                <div key={`backdrop-meter-${width}-${index}`}>
                  <div className="mb-1.5 h-2.5 w-24 rounded-full bg-white/[0.13]" />
                  <div className="h-2.5 rounded-full bg-white/[0.13]">
                    <div className={`h-2.5 rounded-full ${index === 1 ? 'bg-amber-300/75' : 'bg-emerald-300/75'}`} style={{ width: `${width}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-[30px] border border-white/[0.10] px-6 py-5 ${focus === 'household' ? 'bg-violet-400/18' : 'bg-[#182233]'}`}>
            <div className="mb-4 flex items-center justify-between">
              <div className="space-y-3">
                <div className="h-3 w-28 rounded-full bg-white/[0.16]" />
                <div className="h-6 w-36 rounded-full bg-white/[0.22]" />
              </div>
              <div className={`h-10 w-10 rounded-[16px] ${focus === 'household' ? 'bg-violet-300/30' : 'bg-cyan-300/24'}`} />
            </div>
            <div className="space-y-3">
              {[0, 1, 2].map((item) => (
                <div key={`backdrop-household-${item}`} className="flex items-center justify-between rounded-[20px] bg-slate-950/24 px-4 py-3.5">
                  <div className="space-y-2">
                    <div className="h-3 w-28 rounded-full bg-white/[0.16]" />
                    <div className="h-2.5 w-20 rounded-full bg-white/[0.12]" />
                  </div>
                  <div className="h-7 w-20 rounded-full bg-white/[0.14]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute left-[254px] right-[32px] top-[454px] grid gap-6 xl:grid-cols-[1.06fr_0.94fr]">
        <div className="rounded-[32px] border border-white/[0.10] bg-[#182233] px-6 py-5">
          <div className="mb-5 flex items-center justify-between">
            <div className="space-y-3">
              <div className="h-3 w-24 rounded-full bg-white/[0.16]" />
              <div className="h-6 w-44 rounded-full bg-white/[0.22]" />
            </div>
            <div className="h-8 w-24 rounded-full bg-cyan-300/24" />
          </div>
          <div className="flex h-36 items-end gap-3">
            {[22, 30, 28, 42, 50, 60, 72, 66, 80].map((height, index) => (
              <div
                key={`backdrop-trend-${height}-${index}`}
                className={`flex-1 rounded-t-[14px] ${index >= 6 ? 'bg-cyan-300/58' : 'bg-white/[0.18]'}`}
                style={{ height: `${height}%` }}
              />
            ))}
          </div>
        </div>

        <div className="rounded-[32px] border border-white/[0.10] bg-[#182233] px-6 py-5">
          <div className="mb-4 flex items-center justify-between">
            <div className="space-y-3">
              <div className="h-3 w-24 rounded-full bg-white/[0.16]" />
              <div className="h-6 w-36 rounded-full bg-white/[0.22]" />
            </div>
            <div className="h-10 w-10 rounded-[16px] bg-indigo-300/28" />
          </div>
          <div className="space-y-3">
            {[0, 1, 2, 3].map((item) => (
              <div key={`backdrop-feed-${item}`} className="flex items-center justify-between rounded-[20px] bg-slate-950/26 px-4 py-3.5">
                <div className="space-y-2">
                  <div className="h-3 w-28 rounded-full bg-white/[0.16]" />
                  <div className="h-2.5 w-20 rounded-full bg-white/[0.12]" />
                </div>
                <div className="h-3 w-12 rounded-full bg-white/[0.16]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export function PartnerInviteScene({ className = '' }) {
  return (
    <div className={`onboarding-visual onboarding-partner-visual relative overflow-hidden ${className}`}>
      <div className={gridOverlayClassName} />
      <div className="relative space-y-3 p-4 sm:p-5">

        {/* Two panels + animated connector */}
        <div className="grid min-w-0 grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_52px_minmax(0,1fr)] sm:gap-3">

          {/* You panel */}
          <motion.div
            initial={{ opacity: 0, x: -14 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="min-w-0 rounded-2xl border border-[var(--auth-rule)] bg-[var(--auth-panel)] p-3"
          >
            <div className="mb-2.5 flex items-center gap-2">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl border border-[var(--auth-rule)] bg-[var(--color-surface-selected)] text-[11px] font-bold text-[var(--auth-accent)]">Y</div>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--auth-secondary)]">You</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5 sm:block sm:space-y-1.5">
              {[['Whole Foods', '$84'], ['Netflix', '$17'], ['Uber', '$23']].map(([label, amount], i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.1 + i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                  className="flex items-center justify-between rounded-xl border border-[var(--auth-rule)] bg-[var(--auth-panel)] px-2.5 py-1.5"
                >
                  <span className="min-w-0 truncate text-xs text-[var(--auth-secondary)]">{label}</span>
                  <span className="ml-1.5 shrink-0 text-xs font-semibold text-[var(--auth-accent)]">{amount}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Connector — horizontal line ▶ icon forms ▶ line continues */}
          <div className="flex items-center justify-center">

            {/* Mobile: compact horizontal connector */}
            <div className="flex items-center sm:hidden">
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.55, duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: 'left' }}
                className="relative h-0.5 w-8 overflow-hidden rounded-full"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-400/60 to-violet-400/60" />
                <motion.div
                  className="absolute inset-y-0 w-6 bg-gradient-to-r from-transparent via-white/80 to-transparent"
                  animate={{ x: ['-100%', '400%'] }}
                  transition={{ duration: 0.75, delay: 1.1, ease: 'easeInOut', repeat: Infinity, repeatDelay: 3.5 }}
                />
              </motion.div>
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 480, damping: 22, delay: 0.72 }}
                className="mx-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[var(--auth-rule)] bg-[var(--color-surface-selected)]"
              >
                <Users className="h-3.5 w-3.5 text-[var(--auth-accent)]" />
              </motion.div>
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.72, duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: 'left' }}
                className="relative h-0.5 w-8 overflow-hidden rounded-full"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-violet-400/60 to-indigo-400/60" />
                <motion.div
                  className="absolute inset-y-0 w-6 bg-gradient-to-r from-transparent via-white/80 to-transparent"
                  animate={{ x: ['-100%', '400%'] }}
                  transition={{ duration: 0.75, delay: 1.3, ease: 'easeInOut', repeat: Infinity, repeatDelay: 3.5 }}
                />
              </motion.div>
            </div>

            {/* Desktop: full-width connector spanning the 64px column */}
            <div className="relative hidden w-full items-center justify-center overflow-hidden sm:flex">
              {/* left line — draws out from Y */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.55, duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: 'left' }}
                className="h-0.5 w-[20px] flex-shrink-0 rounded-full bg-gradient-to-r from-indigo-400/70 to-violet-400/60"
              />
              {/* icon pops in when left line reaches center */}
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 480, damping: 22, delay: 0.72 }}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[var(--auth-rule)] bg-[var(--color-surface-selected)]"
              >
                <Users className="h-3 w-3 text-[var(--auth-accent)]" />
              </motion.div>
              {/* right line — continues to P */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.72, duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: 'left' }}
                className="h-0.5 w-[20px] flex-shrink-0 rounded-full bg-gradient-to-r from-violet-400/60 to-indigo-400/70"
              />
              {/* shimmer that sweeps the full connector left→right, loops */}
              <motion.div
                className="pointer-events-none absolute inset-y-0 w-10 bg-gradient-to-r from-transparent via-white/75 to-transparent"
                animate={{ x: ['-150%', '350%'] }}
                transition={{ duration: 0.7, delay: 1.15, ease: 'easeInOut', repeat: Infinity, repeatDelay: 3.5 }}
              />
            </div>
          </div>

          {/* Partner panel */}
          <motion.div
            initial={{ opacity: 0, x: 14 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="min-w-0 rounded-2xl border border-[var(--auth-rule)] bg-[var(--auth-panel)] p-3"
          >
            <div className="mb-2.5 flex items-center gap-2">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl border border-[var(--auth-rule)] bg-[var(--color-surface-selected)] text-[11px] font-bold text-[var(--auth-accent)]">P</div>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--auth-secondary)]">Your partner</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5 sm:block sm:space-y-1.5">
              {[['Dining out', '$62'], ['Spotify', '$11'], ['Transit', '$19']].map(([label, amount], i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.22 + i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                  className="flex items-center justify-between rounded-xl border border-[var(--auth-rule)] bg-[var(--auth-panel)] px-2.5 py-1.5"
                >
                  <span className="min-w-0 truncate text-xs text-[var(--auth-secondary)]">{label}</span>
                  <span className="ml-1.5 shrink-0 text-xs font-semibold text-[var(--auth-accent)]">{amount}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
}

export const HouseholdPreviewScene = ({ className = '', hideTitle = false }) => (
  <SceneShell
    eyebrow={hideTitle ? undefined : "Shared clarity"}
    title={hideTitle ? undefined : "Household visibility without the spreadsheet ritual."}
    description={hideTitle ? undefined : "The invite step should show the destination: one shared view, one category system, one place to keep everyone aligned."}
    className={className}
  >
    <div className="grid gap-3 sm:grid-cols-[1.05fr_0.95fr]">
      <div className="rounded-3xl border border-white/[0.08] bg-slate-950/55 p-4">
        <div className="flex items-center gap-3">
          {['F', 'S', 'H'].map((initial, index) => (
            <div
              key={initial}
              className={`flex h-10 w-10 items-center justify-center rounded-2xl border border-white/[0.06] text-sm font-semibold ${index === 0 ? 'bg-indigo-500/15 text-indigo-200' : 'bg-white/[0.04] text-slate-200'}`}
            >
              {initial}
            </div>
          ))}
          <div>
            <p className="text-sm font-semibold text-white">Household members</p>
            <p className="text-xs text-slate-400">Everyone contributes, one view stays consistent.</p>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          {[
            ['Groceries', 'Shared'],
            ['Coffee Shops', 'Jon'],
            ['Streaming', 'Melanie'],
          ].map(([merchant, owner]) => (
            <div key={merchant} className="flex items-center justify-between rounded-2xl border border-white/[0.06] bg-white/[0.03] px-3 py-2">
              <div className="flex items-center gap-3">
                <div className="rounded-xl border border-white/[0.06] bg-slate-900/50 p-2 text-slate-300">
                  <Users className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{merchant}</p>
                  <p className="text-xs text-slate-400">Auto-sorted into the right bucket</p>
                </div>
              </div>
              <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-3 py-1 text-xs text-slate-300">{owner}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-3xl border border-white/[0.08] bg-white/[0.04] p-4">
        <div className="mb-3 flex items-center gap-3">
          <div className="rounded-2xl border border-violet-400/20 bg-violet-500/10 p-2.5 text-violet-200">
            <Users className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Household feed</p>
            <p className="text-xs text-slate-400">Members, categories, and budgets stay in sync.</p>
          </div>
        </div>
        <div className="space-y-3">
          {[
            ['Partner invited', 'Secure sign-in and bank connection'],
            ['Shared budget live', 'Same categories, same spend picture'],
            ['Less maintenance', 'No manual merging later'],
          ].map(([label, body]) => (
            <div key={label} className="rounded-2xl border border-white/[0.06] bg-slate-950/40 px-3 py-3">
              <p className="text-sm font-medium text-white">{label}</p>
              <p className="mt-1 text-xs text-slate-400">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </SceneShell>
);

export const NetWorthPreviewScene = ({ className = '', hideTitle = false }) => (
  <SceneShell
    eyebrow={hideTitle ? undefined : 'Net worth in motion'}
    title={hideTitle ? undefined : 'Connected balances plus the things Plaid can’t see.'}
    description={hideTitle ? undefined : 'The setup flow becomes easier to trust when it shows the shape of the final product before asking for data.'}
    className={className}
  >
    <div className="grid gap-3 sm:grid-cols-[1.1fr_0.9fr]">
      <div className="rounded-3xl border border-white/[0.08] bg-slate-950/55 p-4">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Household net worth</p>
            <p className="mt-1 text-3xl font-bold text-white">$836<span className="text-lg text-white/35">K</span></p>
          </div>
          <div className="rounded-2xl border border-indigo-400/20 bg-indigo-500/10 p-3 text-indigo-200">
            <TrendingUp className="h-5 w-5" />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            ['Cash', '$126K', Wallet],
            ['Investments', '$542K', TrendingUp],
            ['Properties', '$390K', Home],
            ['Liabilities', '-$222K', CreditCard],
          ].map(([label, value, Icon]) => (
            <div key={label} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] px-3 py-3">
              <div className="mb-2 flex items-center gap-2">
                <div className="rounded-xl border border-white/[0.06] bg-slate-900/50 p-2 text-slate-300">
                  <Icon className="h-4 w-4" />
                </div>
                <p className="text-sm font-medium text-white">{label}</p>
              </div>
              <p className={`text-lg font-semibold ${label === 'Liabilities' ? 'text-rose-300' : 'text-white'}`}>{value}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        <div className="rounded-3xl border border-white/[0.08] bg-white/[0.04] p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-2.5 text-emerald-200">
              <Landmark className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Connected accounts</p>
              <p className="text-xs text-slate-400">Cash, credit, and investment balances flow in automatically.</p>
            </div>
          </div>
          <div className="space-y-2">
            {[
              ['RBC Checking', '$23.6K'],
              ['Brokerage', '$128K'],
              ['Mortgage', '$302K'],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between rounded-2xl border border-white/[0.06] bg-slate-950/40 px-3 py-2">
                <span className="text-sm text-white">{label}</span>
                <span className="text-sm font-semibold text-slate-200">{value}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl border border-white/[0.08] bg-white/[0.04] p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 p-2.5 text-amber-200">
              <PiggyBank className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Manual additions</p>
              <p className="text-xs text-slate-400">Property, vehicles, and everything else your bank can’t represent.</p>
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              ['Primary home', '$480K'],
              ['Vehicle', '$24K'],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-white/[0.06] bg-slate-950/40 px-3 py-3">
                <p className="text-sm font-medium text-white">{label}</p>
                <p className="mt-1 text-xs text-slate-400">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </SceneShell>
);

export const NetWorthWindowScene = ({ className = '' }) => (
  <div className={`${shellClassName} ${className}`}>
    <div className={glowClassName} />
    <div className={gridOverlayClassName} />
    <div className="relative overflow-hidden">
      <div className="flex items-center gap-2 border-b border-white/[0.06] px-4 py-3 sm:px-5">
        <span className="h-3 w-3 rounded-full bg-rose-400/80" />
        <span className="h-3 w-3 rounded-full bg-amber-300/80" />
        <span className="h-3 w-3 rounded-full bg-emerald-400/80" />
        <div className="ml-3 flex-1 rounded-xl border border-white/[0.06] bg-white/[0.04] px-4 py-2 text-[11px] font-medium tracking-[0.04em] text-slate-500">
          app.myvylo.com
        </div>
      </div>

      <div className="space-y-5 p-5 sm:p-6">
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Household net worth</p>
          <div className="flex items-end gap-2">
            <p className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
              $836
              <span className="ml-1 text-xl text-white/35 sm:text-2xl">K</span>
            </p>
            <span className="mb-2 inline-flex items-center rounded-full border border-emerald-400/20 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-200">
              up 4.2%
            </span>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          {[
            ['Cash', '$126K', Wallet, 'text-cyan-200', 'border-cyan-400/20 bg-cyan-500/10'],
            ['Investments', '$542K', TrendingUp, 'text-indigo-200', 'border-indigo-400/20 bg-indigo-500/10'],
            ['Liabilities', '-$222K', CreditCard, 'text-rose-200', 'border-rose-400/20 bg-rose-500/10'],
          ].map(([label, value, Icon, iconColor, iconBg]) => (
            <div key={label} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] px-4 py-4">
              <div className="mb-3 flex items-center gap-2">
                <div className={`rounded-xl border p-2 ${iconBg}`}>
                  <Icon className={`h-4 w-4 ${iconColor}`} />
                </div>
                <span className="text-sm font-medium text-slate-300">{label}</span>
              </div>
              <p className={`text-xl font-semibold ${label === 'Liabilities' ? 'text-rose-300' : 'text-white'}`}>{value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const NetWorthInvestmentScene = ({ className = '' }) => (
  <div className={`${shellClassName} ${className}`}>
    <div className={glowClassName} />
    <div className={gridOverlayClassName} />
    <div className="relative overflow-hidden">
      <div className="flex items-center gap-2 border-b border-white/[0.06] px-4 py-3 sm:px-5">
        <span className="h-3 w-3 rounded-full bg-rose-400/80" />
        <span className="h-3 w-3 rounded-full bg-amber-300/80" />
        <span className="h-3 w-3 rounded-full bg-emerald-400/80" />
        <div className="ml-3 flex-1 rounded-xl border border-white/[0.06] bg-white/[0.04] px-4 py-2 text-[11px] font-medium tracking-[0.04em] text-slate-500">
          app.myvylo.com
        </div>
      </div>

      <div className="space-y-5 p-5 sm:p-6">
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Investment accounts</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ['S&P 500', '$18.4K', 'up 8.1%', 'from-indigo-500/20 to-indigo-500/5', 'text-indigo-200'],
              ['Bitcoin', '$6.2K', 'up 12.4%', 'from-amber-500/20 to-amber-500/5', 'text-amber-200'],
            ].map(([label, value, trend, gradient, accent]) => (
              <div key={label} className={`rounded-2xl border border-white/[0.06] bg-gradient-to-br ${gradient} px-4 py-4`}>
                <p className="text-sm font-medium text-slate-300">{label}</p>
                <div className="mt-2 flex items-end justify-between gap-3">
                  <p className="text-2xl font-semibold text-white">{value}</p>
                  <span className={`inline-flex rounded-full border border-white/[0.08] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${accent}`}>
                    {trend}
                  </span>
                </div>
                <div className="mt-4 flex h-16 items-end gap-1.5">
                  {[28, 32, 38, 44, 52, 58].map((height, index) => (
                    <div
                      key={`${label}-${height}`}
                      className={`flex-1 rounded-t-[8px] ${index === 5 ? 'bg-indigo-400/80' : 'bg-white/[0.14]'}`}
                      style={{ height: `${height}%` }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const NetWorthLiabilityScene = ({ className = '' }) => (
  <div className={`${shellClassName} ${className}`}>
    <div className={glowClassName} />
    <div className={gridOverlayClassName} />
    <div className="relative overflow-hidden rounded-[28px]">
      <div className="flex items-center gap-3 border-b border-white/[0.06] px-5 py-4">
        <span className="h-4 w-4 rounded-full bg-rose-400/80" />
        <span className="h-4 w-4 rounded-full bg-amber-300/80" />
        <span className="h-4 w-4 rounded-full bg-emerald-400/80" />
        <div className="ml-4 flex-1 rounded-2xl border border-white/[0.05] bg-white/[0.05] px-4 py-2 text-center text-sm font-semibold tracking-[0.14em] text-slate-500">
          app.myvylo.com
        </div>
      </div>

      <div className="space-y-6 p-6 sm:p-8">
        <div className="text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Liabilities</p>
          <div className="mt-3 flex items-end justify-center gap-3">
            <p className="text-5xl font-bold text-rose-300">$184<span className="text-2xl text-white/35">K</span></p>
            <span className="rounded-full border border-rose-400/25 bg-rose-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-200">
              Owed
            </span>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-3xl border border-white/[0.08] bg-slate-950/55 p-4">
            <div className="mb-3 flex items-center gap-3">
              <div className="rounded-2xl border border-indigo-400/20 bg-indigo-500/10 p-2.5 text-indigo-200">
                <TrendingUp className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Balances over time</p>
                <p className="text-xs text-slate-400">Track what you owe alongside your connected accounts.</p>
              </div>
            </div>
            <div className="flex h-24 items-end gap-2">
              {[52, 60, 72, 68, 84].map((height, index) => (
                <div
                  key={`${height}-${index}`}
                  className={`flex-1 rounded-t-[10px] ${index === 4 ? 'bg-rose-400' : 'bg-rose-400/35'}`}
                  style={{ height: `${height}%` }}
                />
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {[
              ['Student Loan', '$46,000'],
              ['Personal Loan', '$18,500'],
              ['Medical Debt', '$7,200'],
            ].map(([label, amount]) => (
              <div key={label} className="flex items-center justify-between rounded-2xl border border-white/[0.06] bg-white/[0.03] px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="h-2.5 w-2.5 rounded-full bg-rose-300" />
                  <span className="text-sm text-slate-200">{label}</span>
                </div>
                <span className="text-sm font-semibold text-white">{amount}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const NetWorthIntroScene = ({ className = '' }) => (
  <NetWorthPreviewScene className={className} hideTitle />
);
